import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Device from '../../../../../devices/device';
import Metadata from '../../../../../requests/apa/Metadata';
import Translations from '../../../../../requests/apa/Translator';
import Assets from '../../../../../requests/apa/Asset';
import { getMatchesStatistics } from '../../../services/getMatchesStatistics';
import { buildSummaryData } from '../../../utils';
import './_MatchDetailSummary.scss';

// ─── Tipos internos normalizados ──────────────────────────────────────────────
// Estos son los valores finales que usa el componente internamente.
// buildSummaryData (MatchDetailModal) ya produce estos valores.
// RAW_TYPE_MAP convierte el formato nuevo de la API (uppercase) al mismo valor.
export const EVENT_TYPES = {
  GOAL:               'goal',
  SHOT:               'shot',
  SHOT_BLOCKED:       'shot_blocked',
  SHOT_SAVED:         'shot_saved',
  SHOT_OUT:           'shot_out',
  FREE_KICK:          'free_kick',
  CORNER:             'corner',
  FOUL:               'foul',
  OFFSIDE:            'offside',
  YELLOW:             'yellow',
  RED:                'red',
  SECOND_YELLOW_RED_CARD:  'second_yellow_red_card',
  SUBSTITUTION:       'substitution',
  DIVISOR:            'divisor',
  EXTRA_TIME:         'extra_time',
  ADDITIONAL_TIME:    'additional_time',
  END_OF_PERIOD:      'END_OF_PERIOD',
}; 

// Mapeo del API externo (uppercase) → tipo interno normalizado
const RAW_TYPE_MAP = {
  'GOAL':                   'goal',
  'YELLOW_CARD':            'yellow',
  'RED_CARD':               'red',
  'SECOND_YELLOW_RED_CARD': 'second_yellow_red_card',
  'SECOND_YELLOW_CARD':     'red_second_yellow',
  'SUBSTITUTION_IN':        'substitution',
  'SUBSTITUTION_OUT':       'substitution',
  'PERIOD_END':             'divisor',
  'SHOT_OFF_TARGET':        'shot_out',
  'SHOT_ON_TARGET':         'shot',
  'SHOT_BLOCKED':           'shot_blocked',
  'SHOT_SAVED':             'shot_saved',
  'FREE_KICK':              'free_kick',
  'CORNER':                 'corner',
  'FOUL':                   'foul',
  'OFFSIDE':                'offside',
  'EXTRA_TIME':             'extra_time',
  'ADDITIONAL_TIME':        'additional_time',
  'END_OF_PERIOD':          'divisor',
};

// ─── Íconos centralizados ─────────────────────────────────────────────────────
const SUMMARY_ICONS = {
  goal:              () => Assets.get('eventodeportivo_icono_gol_2'),
  bigGoal:           () => Assets.get('eventodeportivo_icono_gol_1'),
  yellow:            () => Assets.get('eventodeportivo_icono_amarilla_2'),
  red:               () => Assets.get('eventodeportivo_icono_roja_2'),
  red_second_yellow: () => Assets.get('eventodeportivo_icono_amarillaroja_2'),
  sub_in:            () => Assets.get('eventodeportivo_icono_entra'),
  sub_out:           () => Assets.get('eventodeportivo_icono_sale'),
};

// ─── Eventos visibles en modo "Jugadas Clave" (toggle OFF) ────────────────────
const SUMMARY_TYPES_WHEN_TOGGLE_OFF = new Set([
  EVENT_TYPES.GOAL,
  EVENT_TYPES.SUBSTITUTION,
  EVENT_TYPES.YELLOW,
  EVENT_TYPES.RED,
  EVENT_TYPES.SECOND_YELLOW_RED_CARD,
  EVENT_TYPES.EXTRA_TIME,
  EVENT_TYPES.ADDITIONAL_TIME,
]);

// Tipos con ícono de tarjeta
const CARD_ICON_TYPES = new Set([
  EVENT_TYPES.YELLOW,
  EVENT_TYPES.RED,
  EVENT_TYPES.RED_SECOND_YELLOW,
  EVENT_TYPES.RED_SECOND_YELLOW,
  EVENT_TYPES.SECOND_YELLOW_RED_CARD,
  EVENT_TYPES.EXTRA_TIME,
]);

// ─── Defaults de equipo ───────────────────────────────────────────────────────
const DEFAULT_HOME_TEAM = {
  name: 'Argentina',
  shortName: 'ARG',
  badgeUrl: '',
  flagUrl: ''
};

const DEFAULT_AWAY_TEAM = {
  name: 'Austria',
  shortName: 'AUT',
  badgeUrl: '',
  flagUrl: ''
};

// ─── Normalización de items ───────────────────────────────────────────────────
// Soporta dos formatos:
//   Formato legacy (buildSummaryData):
//     type: 'goal'|'yellow'|'red'|..., team: 'home'|'away',
//     player: string, playerIn: string, playerOut: string, minute: "23'"
//   Formato nuevo del API (const pp):
//     type: 'GOAL'|'YELLOW_CARD'|..., teamId: 'MEX'|'POR',
//     player: {name: '...'}, relatedPlayer: {name: '...'}, minuteDisplay: '23'

// Extrae nombre de jugador (string o {name: string})
const resolvePlayerName = (p) => {
  if (!p) return '';
  if (typeof p === 'string') return p;
  if (typeof p === 'object' && p.name) return p.name;
  return '';
};

const HOME_PLAYER_MAX_CHARS = 22;
const truncateHomePlayerName = (name) => {
  const safeName = name == null ? '' : String(name);
  if (safeName.length <= HOME_PLAYER_MAX_CHARS) return safeName;
  return safeName.slice(0, HOME_PLAYER_MAX_CHARS) + '...';
};

const EVENT_TITLE_MAX_CHARS = 51;
const truncateEventTitle = (title) => {
  const safeTitle = title == null ? '' : String(title);
  if (safeTitle.length <= EVENT_TITLE_MAX_CHARS) return safeTitle;
  return safeTitle.slice(0, EVENT_TITLE_MAX_CHARS) + '...';
};

const DIVISOR_LABEL_MAX_CHARS = 40;

const truncateDivisorLabel = (label) => {
  const safeLabel = label == null ? '' : String(label);
  return safeLabel.length > DIVISOR_LABEL_MAX_CHARS
    ? safeLabel.slice(0, DIVISOR_LABEL_MAX_CHARS) + '...'
    : safeLabel;
};

const normalizeSummaryItems = (items) => (items || []).map((item) => {
  const rawType = item.type || '';
  // Si el tipo ya es el normalizado (lowercase), RAW_TYPE_MAP devuelve undefined → usamos raw
  const type = RAW_TYPE_MAP[rawType] || rawType;
  const isAdditionalTimeEvent = type === EVENT_TYPES.ADDITIONAL_TIME || rawType === 'ADDITIONAL_TIME';

  // Minuto: si ya tiene ' (formato legacy) se usa tal cual;
  // si no, se construye desde minuteDisplay o minute
  let minute = '';
  if (typeof item.minute === 'string' && item.minute.includes("'")) {
    minute = isAdditionalTimeEvent ? item.minute.replace(/'/g, '') : item.minute;
  } else {
    const val = item.minuteDisplay != null ? item.minuteDisplay : item.minute;
    minute = val != null ? String(val) + (isAdditionalTimeEvent ? '' : "'") : '';
  }

  // Nombre del jugador principal
  const playerName = resolvePlayerName(item.player) || item.playerName || '';

  // Jugadores de sustitución:
  // - Formato legacy: playerIn / playerOut ya son strings planos
  // - Formato nuevo SUBSTITUTION_OUT: player = quien sale, relatedPlayer = quien entra
  // - Formato nuevo SUBSTITUTION_IN:  player = quien entra, relatedPlayer = quien sale
  let playerIn  = item.playerIn  || '';
  let playerOut = item.playerOut || '';
  if (!playerIn && !playerOut) {
    if (rawType === 'SUBSTITUTION_OUT') {
      playerOut = playerName;
      playerIn  = resolvePlayerName(item.relatedPlayer);
    } else if (rawType === 'SUBSTITUTION_IN') {
      playerIn  = playerName;
      playerOut = resolvePlayerName(item.relatedPlayer);
    }
  }

  // Asistencia (solo para goles con relatedPlayer en el formato nuevo)
  const assistName =
    resolvePlayerName(item.assist) ||
    item.playerAssistantName ||
    item.assistName ||
    ((type === EVENT_TYPES.GOAL || rawType === 'GOAL') && item.relatedPlayer ? resolvePlayerName(item.relatedPlayer) : '');

  // Etiqueta para divisores
  const label = item.label || item.name || '';

  // Título descriptivo del evento
  const title = truncateEventTitle(item.title || item.name || '');

  return {
    ...item,
    type,
    _minute:     minute,
    _playerName: playerName,
    _playerIn:   playerIn,
    _playerOut:  playerOut,
    _assistName: assistName,
    _label:      label,
    _title:      title,
  };
});

const getVisibleSummaryItems = (items, isMinuteByMinuteEnabled) => {
  if (isMinuteByMinuteEnabled) return items;
  return items.filter(item =>
    item.type === EVENT_TYPES.DIVISOR ||
    SUMMARY_TYPES_WHEN_TOGGLE_OFF.has(item.type)
  );
};

// ─── Helpers de equipo ────────────────────────────────────────────────────────
const normalizeTeam = (team, fallback) => ({
  ...fallback,
  ...(team || {})
});

const TeamBadge = ({ team }) => (
  <span className="match-detail-summary__top-badge">
    {(team.badgeUrl || team.flagUrl) ? (
      <img src={team.badgeUrl || team.flagUrl} alt={team.name} className="match-detail-summary__top-badge-image" />
    ) : (
      <span className="match-detail-summary__top-badge-text">{team.shortName}</span>
    )}
  </span>
);

// ─── EventIcon ────────────────────────────────────────────────────────────────
// Resuelve src desde SUMMARY_ICONS según tipo normalizado.
// No renderiza si el asset no está disponible.
const EventIcon = ({ type }) => {
  let src = null;
  let className = '';

  if (type === EVENT_TYPES.GOAL) {
    src = SUMMARY_ICONS.goal();
    className = 'match-detail-summary__icon-ball';
  } else if (type === EVENT_TYPES.YELLOW) {
    src = SUMMARY_ICONS.yellow();
    className = 'match-detail-summary__icon match-detail-summary__icon--yellow';
  } else if (type === EVENT_TYPES.RED) {
    src = SUMMARY_ICONS.red();
    className = 'match-detail-summary__icon match-detail-summary__icon--red';
  } else if (type === EVENT_TYPES.SECOND_YELLOW_RED_CARD) {
    src = SUMMARY_ICONS.red_second_yellow();
    className = 'match-detail-summary__icon match-detail-summary__icon--red-second-yellow';
  }

  if (!src) return null;
  return <img className={className} src={src} alt="" aria-hidden="true" />;
};

// ─── EventCell ────────────────────────────────────────────────────────────────
// Renderiza el contenido de la celda home o away.
//
//  GOAL — Figma:
//    3 filas (sin asistencia): jugador / "Gol" / ⚽
//    4 filas (con asistencia): jugador / "Gol" / asistencia / ⚽
//    HOME: [texto][⚽ derecha]   AWAY: [⚽ izquierda][texto]
//
//  SUBSTITUTION:
//    HOME: ["Entra: X" [↑]] / ["Sale: Y" [↓]]   (ícono al final)
//    AWAY: [[↑] "Entra: X"] / [[↓] "Sale: Y"]   (ícono al inicio)
//
//  YELLOW / RED / RED_SECOND_YELLOW:
//    HOME: [jugador / descripción][tarjeta derecha]
//    AWAY: [tarjeta izquierda][jugador / descripción]
//
//  Resto: solo texto.
//
const EventCell = ({ event, side }) => {
  const isAway = side === 'away';
  const playerNameBySide = (playerName) => {
    const safeName = playerName == null ? '' : String(playerName);
    return isAway ? safeName : truncateHomePlayerName(safeName);
  };

  // ── GOL ─────────────────────────────────────────────────────────────────
  if (event.type === EVENT_TYPES.GOAL) {
    const playerName = playerNameBySide(event._playerName);
    const assistName = event._assistName;
    const titleText  = event._title || Translations.get('eventodeportivo_texto_gol') || 'Gol';
    const scoreText  = event.score || '';

    return (
      <div className={`match-detail-summary__event match-detail-summary__event--goal match-detail-summary__event--${side}`}>
        <span className="match-detail-summary__event-with-icon">
          
            <span className="match-detail-summary__event-icon-left">
             {isAway && ( <EventIcon type={EVENT_TYPES.GOAL} /> )}
            </span>
         
          <span className="match-detail-summary__event-text">
            {scoreText ? <span className="match-detail-summary__event-score">{scoreText}</span> : null}
            <span className="match-detail-summary__event-title">{scoreText}</span> 
            <span className={`match-detail-summary__event-player_${side}`}>{playerName}</span>  
            {assistName ? (
              <span className="match-detail-summary__event-assist">
                {'Asistencia'}: {assistName}
              </span>
            ) : null}
          </span>
          
            <span className="match-detail-summary__event-icon-right">
              {!isAway && (<EventIcon type={EVENT_TYPES.GOAL} /> )}
            </span>
         
        </span>
      </div>
    );
  }

  // ── SUSTITUCIÓN ──────────────────────────────────────────────────────────
  if (event.type === EVENT_TYPES.SUBSTITUTION) {
    const playerInName  = event._playerIn;
    const playerOutName = event._playerOut;
    const srcIn  = SUMMARY_ICONS.sub_in();
    const srcOut = SUMMARY_ICONS.sub_out();

    return (
      <div className={`match-detail-summary__event match-detail-summary__event--sub match-detail-summary__event--${side}`}>
       <span className="match-detail-summary__event-title">Cambio</span>       
      {playerInName ? (
          <span className="match-detail-summary__event-sub-row">
            <img className="match-detail-summary__icon-arrow match-detail-summary__icon-arrow--in" src={srcIn} alt="" aria-hidden="true" />
            <span className="match-detail-summary__event-player">
              {'Entra'}: {playerInName}
            </span>
          </span>
        ) : null}
        {playerOutName ? (
          <span className="match-detail-summary__event-sub-row">
            <img className="match-detail-summary__icon-arrow match-detail-summary__icon-arrow--out" src={srcOut} alt="" aria-hidden="true" />
            <span className="match-detail-summary__event-player">
              {'Sale'}: {playerOutName}
            </span>
          </span>
        ) : null}
      </div>
    );
  }

  // ── TARJETAS (YELLOW / RED / RED_SECOND_YELLOW) ──────────────────────────
  if (CARD_ICON_TYPES.has(event.type)) {
    const playerName = playerNameBySide(event._playerName);
    const descText   = event._title;

    return (
      <div className={`match-detail-summary__event match-detail-summary__event--card match-detail-summary__event--${side}`}>
        <span className="match-detail-summary__event-with-icon">
          {isAway && (
            <span className="match-detail-summary__event-icon-left">
              <EventIcon type={event.type} />
            </span>
          )}
          <span className="match-detail-summary__event-text">
          {descText ? <span className="match-detail-summary__event-title">{descText}</span> : null}
            <span className={`match-detail-summary__event-player_${side}`}>{playerName}</span>
          </span>
          {!isAway && (
            <span className="match-detail-summary__event-icon-right">
              <EventIcon type={event.type} />
            </span>
          )}
        </span>
      </div>
    );
  }

  // ── RESTO (SHOT, FREE_KICK, CORNER, FOUL…) ───────────────────────────────
  const playerName = playerNameBySide(event._playerName);
  const descText   = event._title;

  return (
    <div className={`match-detail-summary__event match-detail-summary__event--${side}`}>
      <span className={`match-detail-summary__event-text_${side}`}>
      {descText ? <span className="match-detail-summary__event-title">{descText}</span> : null}
        <span className={`match-detail-summary__event-player_${side}`}>{playerName}</span>        
      </span>
    </div>
  );
};

class MatchDetailSummary extends Component {
  constructor(props) {
    super(props);
    this.keys = Device.getDevice && Device.getDevice().getKeys ? Device.getDevice().getKeys() : null;
    this.state = {
      isMinuteByMinuteEnabled: false,
      summaryFromApi: []
    };
    this.leagueId = props.leagueId;
    this.matchId = props.matchId;
    this._isMounted = false;
    this._summaryRequestId = 0;
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps) {
    const leagueChanged = prevProps.leagueId !== this.props.leagueId;
    const matchChanged = prevProps.matchId !== this.props.matchId;

    if (leagueChanged || matchChanged) {
      this.leagueId = this.props.leagueId;
      this.matchId = this.props.matchId;
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  fetchSummaryFromApi = async () => {
    if (!this.leagueId || !this.matchId) return;

    const requestId = ++this._summaryRequestId;
    const view = 'live-events';

    try {
      const matchSummary = await getMatchesStatistics(this.leagueId, this.matchId, view);
      if (!this._isMounted || requestId !== this._summaryRequestId) return;

      const mappedSummary = buildSummaryData(matchSummary);
      this.setState({ summaryFromApi: Array.isArray(mappedSummary) ? mappedSummary : [] });
    } catch (error) {
      if (!this._isMounted || requestId !== this._summaryRequestId) return;
      this.setState({ summaryFromApi: [] });
    }
  }

  getItemsToRender = () => {
    const { data, liveMatchActions } = this.props;
    const { items } = this.props;
    const { summaryFromApi } = this.state;
    // Base items: usar summaryFromApi si existe, sino usar data del props
    let baseItems = Array.isArray(summaryFromApi) && summaryFromApi.length > 0 ? summaryFromApi : (data || []);
    
    // Si hay acciones en vivo, mergearlas con baseItems
    if (Array.isArray(liveMatchActions) && liveMatchActions.length > 0) {
      // Crear set de IDs existentes para evitar duplicados
      const existingIds = new Set(baseItems.map(item => item.id || item.actionId || item.eventId).filter(Boolean));
      
      // Filtrar liveMatchActions que no estén ya en baseItems
      const newLiveActions = liveMatchActions.filter(action => {
        const actionId = action.id || action.actionId || action.eventId;
        return actionId && !existingIds.has(actionId);
      });
      
      // Convertir liveMatchActions al formato esperado por normalizeSummaryItems
      const formattedLiveActions = newLiveActions.map(action => ({
        type: action.type,
        minute: action.minute,
        second: action.second,
        player: action.player,
        team: action.team,
        teamId: action.teamId,
        participantId: action.participantId,
        title: action.typeLabel || action.description,
        name: action.typeLabel || action.description,
        id: action.id,
        actionId: action.id,
        eventId: action.id,
        description: action.description,
        streamEventId: action.streamEventId || null,
        eventId: action.eventId || null,
        matchId: action.matchId,
        eventType: action.eventType || null,
        eventVersion: action.eventVersion || null,
        topic: action.topic || null,
        occurredAt: action.occurredAt || null,
        receivedAt: action.receivedAt || null,
        timestamp: action.timestamp || null,
        order: action.order || null,
        time: action.time || null,
        minuteDisplay: action.minuteDisplay,
        periodNumber: action.periodNumber || null,
        periodType: action.periodType || null,
        participantName: action.participantName || null,
        participantType: action.participantType || null,
        participantNumber: action.participantNumber || null,
        score: action.score || null,
        scoreText: action.scoreText || null,
        playerIn: action.playerIn || null,
        playerOut: action.playerOut || null,
        relatedPlayerName: action.relatedPlayerName || null
      }));
      
      // Mergear: liveActions primero (más recientes) + baseItems
      return [...formattedLiveActions, ...baseItems];
    }
    
    return baseItems;
  }

  getPressedKey = (e) => {
    const mappedKey = this.keys && this.keys.getPressKey ? this.keys.getPressKey(e.keyCode) : null;
    if (mappedKey) return mappedKey;
    if (e.key === 'ArrowLeft') return 'LEFT';
    if (e.key === 'ArrowRight') return 'RIGHT';
    if (e.key === 'ArrowDown') return 'DOWN';
    if (e.key === 'ArrowUp') return 'UP';
    return null;
  };

  handleToggleMinuteByMinute = () => {
    this.setState(prevState => ({
      isMinuteByMinuteEnabled: !prevState.isMinuteByMinuteEnabled
    }));
  };

  handleToggleKeyDown = (e) => {
    const currentKey = this.getPressedKey(e);
    const toggleSelector = '#match-detail-summary-toggle';

    if (currentKey === 'LEFT' || currentKey === 'RIGHT') {
      e.preventDefault();
      e.stopPropagation();
      if (window.SpatialNavigation && typeof window.SpatialNavigation.focus === 'function') {
        window.SpatialNavigation.focus(toggleSelector);
      } else if (e.currentTarget && e.currentTarget.focus) {
        e.currentTarget.focus();
      }
      return;
    }

    if (currentKey === 'DOWN') {
      e.preventDefault();
      e.stopPropagation();
      var content = document.querySelector('.match-detail-body__content');
      if (content) {
        content.scrollTop += 120;
      }
    }
  };

  render() {
    const { homeTeam, awayTeam } = this.props;
    const { isMinuteByMinuteEnabled } = this.state;
    const sourceItems = this.getItemsToRender();
    const normalizedItems = normalizeSummaryItems(sourceItems);
    const visibleItems = getVisibleSummaryItems(normalizedItems, isMinuteByMinuteEnabled).slice();
    const safeHomeTeam = normalizeTeam(homeTeam, DEFAULT_HOME_TEAM);
    const safeAwayTeam = normalizeTeam(awayTeam, DEFAULT_AWAY_TEAM);

    return (
      <div className="match-detail-summary">
        <div className="match-detail-summary__top-sticky">
          <div className="match-detail-summary__top">
            <div className="match-detail-summary__top-col match-detail-summary__top-col--home">
              <span className="match-detail-summary__top-team">
                <TeamBadge team={safeHomeTeam} />
                <span className="match-detail-summary__top-team-name">{safeHomeTeam.name}</span>
              </span>
            </div>

            <div className="match-detail-summary__top-col match-detail-summary__top-col--center">
              <span className="match-detail-summary__top-label">{Translations.get('eventodeportivo_partidos_resumen_texto_titulo_verminutoaminuto')}</span>
              <a
                id="match-detail-summary-toggle"
                role="button"
                tabIndex="0"
                href="#"
                className={`focusable match-detail-summary__top-switch ${isMinuteByMinuteEnabled ? 'match-detail-summary__top-switch--on' : ''}`}
                aria-pressed={isMinuteByMinuteEnabled}
                data-sn-left="#match-detail-summary-toggle"
                data-sn-right="#match-detail-summary-toggle"
                data-sn-down=".match-detail-body__content"
                onClick={(e) => { e.preventDefault(); this.handleToggleMinuteByMinute(); }}
                onKeyDown={this.handleToggleKeyDown}
              >
                <span className="match-detail-summary__top-switch-thumb" />
              </a>
            </div>

            <div className="match-detail-summary__top-col match-detail-summary__top-col--away">
              <span className="match-detail-summary__top-team">
                <span className="match-detail-summary__top-team-name">{safeAwayTeam.name}</span>
                <TeamBadge team={safeAwayTeam} />
              </span>
            </div>
          </div>
        </div>

        {visibleItems.map((item, index) => {
          // ── Divisores ────────────────────────────────────────────────────
          if (item.type === EVENT_TYPES.DIVISOR || item.type === EVENT_TYPES.END_OF_PERIOD) {
            return (
              <div key={'divisor-' + index} className="match-detail-summary__divisor">
                <span className="match-detail-summary__divisor-label">{truncateDivisorLabel(item._label)}</span>
              </div>
            );
          }

          // ── Determinar equipo ────────────────────────────────────────────
          // Prioridad: item.team ('home'/'away') del formato legacy.
          // Fallback: comparar item.teamId con homeTeam id/shortName.
          let isHomeTeam = false;
          if (item.team === 'home' || item.team === 'away') {
            isHomeTeam = item.team === 'home';
          } else {
            const homeId = safeHomeTeam.teamId || safeHomeTeam.id || safeHomeTeam.shortName || '';
            isHomeTeam = homeId && item.teamId ? item.teamId === homeId : false;
          }

          // ── Conector ─────────────────────────────────────────────────────
          const isTallConnector = item.type === EVENT_TYPES.GOAL || item.type === EVENT_TYPES.SUBSTITUTION;
          const nextItem = visibleItems[index + 1];
          const isLastItem = index === visibleItems.length - 1;
          const showConnector = !isLastItem && nextItem && nextItem.type !== EVENT_TYPES.DIVISOR;
          const minuteClassName = item.type === EVENT_TYPES.ADDITIONAL_TIME
            ? 'match-detail-summary__minute-additional-time'
            : (item.type === EVENT_TYPES.EXTRA_TIME
              ? 'match-detail-summary__minute-extra-time'
              : 'match-detail-summary__minute');

          return (
            <div key={(item.eventId || item._minute || '') + '-' + index}>
              <div className="match-detail-summary__row">
                <div className="match-detail-summary__col match-detail-summary__col--home">
                  {isHomeTeam && <EventCell event={item} side="home" />}
                </div>
                <div className="match-detail-summary__col match-detail-summary__col--minute">
                  <span className={minuteClassName}>{item._minute}</span>
                </div>
                <div className="match-detail-summary__col match-detail-summary__col--away">
                  {!isHomeTeam && <EventCell event={item} side="away" />}
                </div>
              </div>
              {showConnector && (
                <div className={`match-detail-summary__connector${isTallConnector ? ' match-detail-summary__connector--tall' : ' match-detail-summary__connector--short'}`} />
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

MatchDetailSummary.propTypes = {
  data: PropTypes.array,
  items: PropTypes.array,
  liveMatchActions: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    typeLabel: PropTypes.string,
    minute: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    second: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    player: PropTypes.string,
    team: PropTypes.string,
    teamId: PropTypes.string,
    participantId: PropTypes.string,
    description: PropTypes.string,
  })),
  leagueId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  matchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  homeTeam: PropTypes.shape({
    name: PropTypes.string,
    shortName: PropTypes.string,
    badgeUrl: PropTypes.string,
    flagUrl: PropTypes.string
  }),
  awayTeam: PropTypes.shape({
    name: PropTypes.string,
    shortName: PropTypes.string,
    badgeUrl: PropTypes.string,
    flagUrl: PropTypes.string
  })
};

MatchDetailSummary.defaultProps = {
  data: [],
  items: [],
  liveMatchActions: [],
  leagueId: null,
  matchId: null,
  homeTeam: DEFAULT_HOME_TEAM,
  awayTeam: DEFAULT_AWAY_TEAM
};

export default MatchDetailSummary;


// WEBPACK FOOTER //
// ./src/containers/Mundial2026/FootballMatchDetail/Components/MatchDetailSummary/MatchDetailSummary.jsx